/**
 * Tracks API reachability from axios success/failure (dev + production).
 * Opens global UI when the server is down, hung, or returning gateway errors.
 */

export const CSRPE_API_CONNECTIVITY_EVENT = "csrp:api-connectivity";

/** Failures within this window count toward showing the modal. */
const FAILURE_WINDOW_MS = 30_000;
/** Consecutive/windowed failures before blocking UI. */
const FAILURE_THRESHOLD = 2;

let failureTimestamps = [];
let lastFailureMessage = "";
let modalOpen = false;

const pruneFailures = (now) => {
  failureTimestamps = failureTimestamps.filter((t) => now - t < FAILURE_WINDOW_MS);
};

const emit = (detail) => {
  try {
    window.dispatchEvent(
      new CustomEvent(CSRPE_API_CONNECTIVITY_EVENT, { detail })
    );
  } catch {
    // SSR / tests
  }
};

const isRequestCanceled = (error) => {
  if (!error) return false;
  if (error.code === "ERR_CANCELED") return true;
  if (error.name === "CanceledError" || error.name === "AbortError") return true;
  if (error.__CANCEL__ === true) return true; // axios legacy cancel
  const msg = String(error.message || "").toLowerCase();
  return msg.includes("canceled") || msg.includes("cancelled") || msg.includes("aborted");
};

const isConnectivityFailure = (error) => {
  if (!error || error.isSessionExpired) return false;
  if (error.isUnauthorized || error.isForbidden) return false;
  // AbortController / axios cancel must NOT open the network modal — dashboard
  // and list navigation intentionally cancel in-flight calls while data still loads.
  if (isRequestCanceled(error)) return false;
  if (error.isTimeout === true) return true;
  if (error.code === "ECONNABORTED" && String(error.message || "").includes("timeout")) {
    return true;
  }
  if (
    error.code === "ERR_NETWORK" ||
    String(error.message || "").includes("Network Error")
  ) {
    return true;
  }
  // No HTTP response usually means the API is down — but not for cancellations (handled above).
  if (error.response == null) {
    return true;
  }
  const status = error.response?.status;
  return status === 502 || status === 503 || status === 504;
};

const describeFailure = (error) => {
  if (error?.isTimeout) {
    return "The server did not respond in time. It may be busy or stopped.";
  }
  if (error?.response?.status === 504) {
    return "The server timed out processing your request.";
  }
  if (error?.response?.status === 502 || error?.response?.status === 503) {
    return "The server is temporarily unavailable.";
  }
  if (
    error?.code === "ERR_NETWORK" ||
    String(error?.message || "").includes("Network Error")
  ) {
    return "Cannot reach the CSRP API. Check that the API service is running.";
  }
  return error?.message || "Cannot reach the server.";
};

/**
 * Call from axios response success interceptor (non-exempt requests).
 */
export const recordApiSuccess = (config) => {
  if (config?.__skipConnectivityTrack) return;
  failureTimestamps = [];
  if (modalOpen) {
    modalOpen = false;
    emit({ open: false, reason: "recovered" });
  }
};

/**
 * Call from axios error interceptor after retries are exhausted.
 */
export const recordApiFailure = (error, config) => {
  if (config?.__skipConnectivityTrack) return;
  if (!isConnectivityFailure(error)) return;

  const now = Date.now();
  pruneFailures(now);
  failureTimestamps.push(now);
  lastFailureMessage = describeFailure(error);

  if (failureTimestamps.length >= FAILURE_THRESHOLD) {
    modalOpen = true;
    emit({
      open: true,
      message: lastFailureMessage,
      failureCount: failureTimestamps.length,
    });
  }
};

export const getLastApiConnectivityMessage = () => lastFailureMessage;

export const resetApiConnectivityState = () => {
  failureTimestamps = [];
  lastFailureMessage = "";
  modalOpen = false;
  emit({ open: false, reason: "reset" });
};
